import React, { useState, useEffect } from 'react';

// Cookie Configuration
const CONSENT_COOKIE_NAME = 'CookieConsent';
const COOKIE_EXPIRY_DAYS = 365;

// Always enabled cookie types
const ALWAYS_ENABLED_TYPES = ['essential', 'functionality_storage', 'security_storage', 'personalization_storage'];

// ------------------------
// Cookie Utility Functions
// ------------------------
const setCookie = (name, value, days) => {
  if (typeof document === 'undefined') return;
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  }
  const encodedValue = encodeURIComponent(value || "");
  document.cookie = name + "=" + encodedValue + expires + "; path=/; SameSite=Lax";
};

const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      try {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      } catch (e) {
        return c.substring(nameEQ.length, c.length);
      }
    }
  }
  return null;
};

// ------------------------
// Geo Utility Functions
// ------------------------
const getGeoData = () => {
  if (typeof window === 'undefined') return { strict: true };
  try {
    const geoCookie = getCookie('user-geo');
    if (!geoCookie) return { strict: true };
    return JSON.parse(geoCookie);
  } catch (error) {
    return { strict: true };
  }
};

// ------------------------
// Consent Utility Functions
// ------------------------
export const getDefaultPreferences = () => {
  const geoData = getGeoData();
  const isStrictRegion = geoData.strict === true;
  return {
    essential: true,
    analytics: !isStrictRegion,
    advertising: false,
    functionality_storage: true,
    security_storage: true,
    personalization_storage: true,
    consentGiven: false
  };
};

export const hasUserMadeChoice = () => {
  if (typeof window === 'undefined') return false;
  return getCookie(CONSENT_COOKIE_NAME) !== null;
};

export const getStoredPreferences = () => {
  const consentCookieString = getCookie(CONSENT_COOKIE_NAME);
  if (!consentCookieString) return null;

  try {
    const cookieData = JSON.parse(consentCookieString);
    return {
      essential: true,
      analytics: cookieData.statistics === true,
      advertising: cookieData.marketing === true,
      functionality_storage: true,
      security_storage: true,
      personalization_storage: true,
      consentGiven: true,
      timestamp: cookieData.timestamp ? new Date(cookieData.timestamp).toISOString() : new Date().toISOString(),
    };
  } catch (error) {
    console.error('[Consent] Error parsing stored preferences:', error);
    return null;
  }
};

export const hasConsent = (type) => {
  if (typeof window === 'undefined') return ALWAYS_ENABLED_TYPES.includes(type);
  
  const storedPrefs = getStoredPreferences();
  if (storedPrefs) {
    return storedPrefs[type] === true;
  }
  
  const defaultPrefs = getDefaultPreferences();
  return defaultPrefs[type] === true;
};

export const getAllConsents = () => {
  if (typeof window === 'undefined') return getDefaultPreferences();
  
  const storedPrefs = getStoredPreferences();
  if (storedPrefs) {
    return {
      ...getDefaultPreferences(),
      ...storedPrefs,
      essential: true,
      functionality_storage: true,
      security_storage: true,
      personalization_storage: true,
      consentGiven: true
    };
  }
  return getDefaultPreferences();
};

export const saveConsents = (preferences) => {
  if (typeof window === 'undefined') return;

  const newConsents = {
    ...getAllConsents(),
    ...preferences,
    essential: true,
    functionality_storage: true,
    security_storage: true,
    personalization_storage: true,
    consentGiven: true
  };

  const cookieData = {
    necessary: true,
    statistics: newConsents.analytics === true,
    marketing: newConsents.advertising === true,
    functionality_storage: true,
    security_storage: true,
    personalization_storage: true,
    timestamp: new Date().getTime(),
    region: '',
  };

  setCookie(CONSENT_COOKIE_NAME, JSON.stringify(cookieData), COOKIE_EXPIRY_DAYS);
  
  console.log('[CONSENT] About to call updateGTMConsent with:', newConsents.analytics, newConsents.advertising);
  updateGTMConsent(newConsents.analytics, newConsents.advertising);
  
  console.log('[Consent] Saved preferences:', newConsents);
};

// ------------------------
// GTM Consent Function
// ------------------------
export const updateGTMConsent = (analyticsConsent, advertisingConsent) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('[GTM] gtag not available, skipping consent update.');
    return;
  }

  const analyticsGranted = analyticsConsent ? 'granted' : 'denied';
  const advertisingGranted = advertisingConsent ? 'granted' : 'denied';

  const consentState = {
    'analytics_storage': analyticsGranted,
    'ad_storage': advertisingGranted,
    'ad_user_data': advertisingGranted,
    'ad_personalization': advertisingGranted,
    'functionality_storage': 'granted',
    'security_storage': 'granted',
    'personalization_storage': 'granted'
  };

  console.log('[GTM] Updating consent:', consentState);
  window.gtag('consent', 'update', consentState);
  
  window.gtag('set', 'ads_data_redaction', !advertisingConsent);
  window.gtag('set', 'url_passthrough', !advertisingConsent);

  // Debug: check dataLayer after consent update
  setTimeout(() => {
    console.log('[GTM DEBUG] DataLayer after consent:', window.dataLayer.slice(-5));
  }, 100);
};

// ------------------------
// Initialization Logic
// ------------------------
export const initializeConsentServices = () => {
    if (typeof window === 'undefined') return;

    console.log('[ConsentManager] Initializing consent services...');
    
    if (hasUserMadeChoice() && window.gtag) {
        const consents = getAllConsents();
        updateGTMConsent(consents.analytics, consents.advertising);
    }
};

// Simple ConsentManager component using CookieBanner
export const ConsentManager = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!hasUserMadeChoice()) {
      setShowBanner(true);
    }
    initializeConsentServices();
  }, []);

  const handleBannerClose = () => {
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto z-50">
      <div className="bg-white w-full md:max-w-md rounded-xl shadow-2xl ml-auto">
        <div className="p-6">
          <h2 className="text-lg font-bold mb-1">Cookies managing</h2>
          <p className="mb-6 text-sm">
            We use cookies to provide the best site experience. By continuing to use the site, you consent to our{' '}
            <a href="https://davooda.com/privacy" target="_blank" rel="noopener noreferrer"
               className="text-black underline hover:no-underline">cookie policy</a>.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={() => { saveConsents({ analytics: true, advertising: true }); handleBannerClose(); }}
              className="bg-black text-white px-6 py-3 rounded text-sm w-full sm:w-auto"
            >
              Accept All
            </button>
            <button 
              onClick={() => { saveConsents({ analytics: false, advertising: false }); handleBannerClose(); }}
               className="bg-white border border-black text-black px-6 py-3 rounded text-sm w-full sm:w-auto"
            >
              Reject All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export as default object AND named exports for compatibility
const ConsentUtils = {
  hasConsent,
  hasUserMadeChoice,
  getAllConsents,
  saveConsents,
  updateGTMConsent,
  getStoredPreferences,
  initializeConsentServices,
  getDefaultPreferences,
  ConsentManager
};

export default ConsentUtils;